/**
 * SpeedmasterWarranty.Shopping.js
 * Main entry point for the SpeedmasterWarranty extension (shopping app).
 *
 * Uses SuiteCommerce Extensibility API throughout:
 *   - Layout.addToViewContextDefinition — injects warranty data into SKU view
 *   - PDP.addChildViews — mounts warranty widget above Add to Cart
 *   - Delegated click handler for cart-line checkbox card (warranty-toggle)
 *   - Cart modules mounted via Cart component
 */
define('SpeedmasterWarranty.Shopping', [
    'SpeedmasterWarranty.Config',
    'SpeedmasterWarranty.PDP.View',
    'SpeedmasterWarranty.Cart',
    'SpeedmasterWarranty.Cart.Confirmation',
    'jQuery'
], function SpeedmasterWarrantyShoppingDef(
    WarrantyConfig,
    PDPView,
    WarrantyCart,
    WarrantyCartConfirmation,
    jQuery
) {
    'use strict';

    // SVG icons for checkbox card visual toggle (cart-line delegated handler)
    var CHECKED_SVG = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="1" width="16" height="16" rx="3" fill="#2E86C1" stroke="#2E86C1" stroke-width="1.5"/><path d="M5 9l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var UNCHECKED_SVG = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="1" y="1" width="16" height="16" rx="3" stroke="#ccc" stroke-width="1.5" fill="none"/></svg>';

    /**
     * Toggle visual state of a checkbox card.
     * Used for cart-line warranty-toggle action (delegated click handler).
     * Direct DOM update for instant feedback — no re-render needed.
     * @param {jQuery} $card  The .speedmaster-warranty-pdp-card element
     * @param {boolean} isActive  New state
     */
    function _toggleVisual($card, isActive) {
        if (isActive) {
            $card.addClass('is-active').attr('aria-checked', 'true');
            $card.find('.speedmaster-warranty-pdp-check').html(CHECKED_SVG);
        } else {
            $card.removeClass('is-active').attr('aria-checked', 'false');
            $card.find('.speedmaster-warranty-pdp-check').html(UNCHECKED_SVG);
        }
    }

    return {

        mountToApp: function mountToApp(container) {
            var cfg = WarrantyConfig.get();
            if (!cfg.enabled) { return; }

            // 1. Inject warranty context into SKU view (for cart-line rendering)
            this._addSkuViewContext(container);

            // 2. Mount PDP warranty widget above Add to Cart
            this._mountPDPView(container);

            // 3. Bind delegated click handler for cart-line checkbox card toggle
            this._bindToggleClicks();

            // 4. Mount Cart modules (summary panel)
            WarrantyCart.mountToApp(container);

            // 5. Confirmation popup
            WarrantyCartConfirmation.loadModule();
        },

        /**
         * Use Layout.addToViewContextDefinition to inject warranty data
         * into the ProductLine.Sku.View context. This provides data for
         * the warranty checkbox card rendered in cart line items via the
         * product_line_sku.tpl template override.
         *
         * The SKU view renders in multiple contexts (PDP, cart, mini-cart,
         * modals). The template always renders the warranty card when
         * showOption is true. CSS hides the duplicate on PDP (where the
         * real warranty widget is mounted via addChildViews) and in
         * mini-cart/modals.
         *
         * This is the correct trade-off: instant render (no flicker) on
         * cart lines, one CSS hide rule for PDP. DOM injection was tried
         * and rejected — badges disappeared on every cart update/re-render.
         * @private
         */
        _addSkuViewContext: function _addSkuViewContext(container) {
            var layout = container.getComponent('Layout');
            if (!layout) { return; }

            var cfg = WarrantyConfig.get();

            // Pre-compute term badge and description (same for all items)
            var years = cfg.termYears || 3;
            var termBadge = years + ' Year' + (years > 1 ? 's' : '');
            var warrantyDescription = years + '-year extended coverage beyond the manufacturer warranty';

            var safeDefault = {
                enabled: false,
                isEligible: false,
                showOption: false,
                itemId: '',
                warrantyCost: 0,
                warrantyCostFormatted: '',
                warrantyIsActive: false,
                termLabel: cfg.termLabel,
                termBadge: termBadge,
                warrantyDescription: warrantyDescription
            };

            layout.addToViewContextDefinition(
                'ProductLine.Sku.View',
                'speedmasterWarranty',
                'object',
                function speedmasterWarrantyContext(context) {
                    try {
                        var model = context && context.model;
                        var item = model && (model.item || (model.get && model.get('item')));
                        if (!item) { return safeDefault; }

                        // Handle both POJO and Backbone model for item
                        var itemAttrs = (item.attributes) ? item.attributes : item;
                        var itemId = (itemAttrs.internalid || '').toString();
                        if (!itemId) { return safeDefault; }

                        var fieldValue = itemAttrs[cfg.eligibilityField];
                        var isEligible = !!fieldValue && fieldValue !== 'F' && fieldValue !== 'false' && fieldValue !== 'N' && fieldValue !== false;

                        var itemPrice = 0;
                        var priceDetail = itemAttrs.onlinecustomerprice_detail;
                        if (priceDetail && priceDetail.onlinecustomerprice) {
                            itemPrice = Number(priceDetail.onlinecustomerprice) || 0;
                        }

                        var warrantyCost = (isEligible && itemPrice > 0)
                            ? WarrantyConfig.calcLineWarrantyCost(itemPrice) : 0;

                        return {
                            enabled: cfg.enabled,
                            isEligible: isEligible,
                            termLabel: cfg.termLabel,
                            termBadge: termBadge,
                            warrantyCost: warrantyCost,
                            warrantyCostFormatted: warrantyCost > 0 ? WarrantyConfig.formatPrice(warrantyCost) : '',
                            warrantyDescription: warrantyDescription,
                            showOption: isEligible && warrantyCost > 0,
                            warrantyIsActive: WarrantyConfig.isItemOpted(itemId),
                            itemId: itemId
                        };
                    } catch (e) {
                        return safeDefault;
                    }
                }
            );
        },

        /**
         * Mount the PDP warranty view as a child view of ProductDetails.Full.View
         * using the PDP component's addChildViews API.
         *
         * Targets CMS area 4 (product_details_full_cms_area_4) which is
         * directly before the Add to Cart section in the PDP template.
         * @private
         */
        _mountPDPView: function _mountPDPView(container) {
            var pdp = container.getComponent('PDP');
            if (!pdp) { return; }

            // Store PDP component ref so PDP.View can access it via Config
            WarrantyConfig.setPdpComponent(pdp);

            pdp.addChildViews('ProductDetails.Full.View', {
                'cms:product_details_full_cms_area_4': {
                    'SpeedmasterWarrantyPDP': {
                        childViewConstructor: PDPView,
                        childViewOptions: { pdpComponent: pdp }
                    }
                }
            });
        },

        /**
         * Delegated click handler for cart-line checkbox card toggle.
         * Handles the warranty-toggle action rendered in the SKU template
         * on cart line items. PDP toggle is handled by PDP.View's own events.
         * @private
         */
        _bindToggleClicks: function _bindToggleClicks() {
            jQuery(document).off('.speedmasterWarrantyToggle');

            jQuery(document).on(
                'click.speedmasterWarrantyToggle',
                '[data-action="warranty-toggle"]',
                function onToggleClick(e) {
                    // Let info link clicks navigate — don't toggle
                    if (jQuery(e.target).closest('a').length) { return; }

                    // Skip if inside PDP view (PDP.View handles its own toggle)
                    if (jQuery(this).closest('.product-details-full-zone-action').length) { return; }

                    e.preventDefault();
                    e.stopPropagation();

                    var $card = jQuery(this);
                    var itemId = $card.closest('[data-sw-item-id]').attr('data-sw-item-id');
                    var isCurrentlyActive = $card.hasClass('is-active');
                    var newState = !isCurrentlyActive;

                    _toggleVisual($card, newState);

                    if (itemId) {
                        WarrantyConfig.setItemOpted(itemId, newState);
                    }

                    // Trigger Cart Summary recalculation (only on cart pages)
                    jQuery(document).trigger('speedmasterWarranty:itemToggled');
                }
            );

            // Keyboard support for cart-line checkbox cards
            jQuery(document).on(
                'keydown.speedmasterWarrantyToggle',
                '[data-action="warranty-toggle"]',
                function onToggleKeydown(e) {
                    if (e.which === 13 || e.which === 32) {
                        e.preventDefault();
                        jQuery(this).trigger('click');
                    }
                }
            );
        },

        /* ── Public API (delegates to Config helpers) ──────────── */

        getItemMap:       WarrantyConfig.getItemMap,
        setItemOpted:     WarrantyConfig.setItemOpted,
        isItemOpted:      WarrantyConfig.isItemOpted,
        clearItemMap:     WarrantyConfig.clearItemMap,
        hasAnyOptedItems: WarrantyConfig.hasAnyOptedItems

    };
});
